import React, { createContext, useContext, useState, useRef } from 'react';
import { MessageBarType } from "@fluentui/react";
import { BwjToast } from '.';

const defaultToastDisplayTime = 3000;

export interface IBwjToastContextProps {
  showToast: (type: MessageBarType, message: string, multiline?: boolean, autoDismissTime?: number) => void;
  dismissToast: () => void;
}

interface IToastDisplayProps {
  type: MessageBarType;
  message: string;
  multiline: boolean;
  visible: boolean;
}
  
  // Create a context for the toast
  const BwjToastContext = createContext<IBwjToastContextProps | undefined>(undefined);
  
  // Create a provider component to manage the toast state
  export const BwjToastProvider: React.FC = ({ children }) => {
    const [toast, setToast] = useState<IToastDisplayProps>({
      type: MessageBarType.info,
      message: '',
      multiline: false,
      visible: false,
    });

    const dismissTimeout = useRef<number | undefined>();
  
  
    // Function to dismiss the toast
    const dismissToast = () => {
      if(!!dismissTimeout.current) {
          window.clearTimeout(dismissTimeout.current);
          dismissTimeout.current = undefined;
      }
      
      setToast(prevToast => ({
        ...prevToast,
        message: '',
        visible: false,
      }));
    };

    // Function to show the toast
    const showToast = (
      type: MessageBarType,
      message: string,
      multiline: boolean = false,
      autoDismissTime: number = defaultToastDisplayTime
    ) => {
      if(autoDismissTime > 0) {
        dismissTimeout.current = window.setTimeout(dismissToast, autoDismissTime);
      }

      setToast({
        type,
        message,
        multiline,
        visible: true,
      });
    };
  
    return (
      <BwjToastContext.Provider value={{ showToast, dismissToast }}>
        {children}
        {toast.visible && <BwjToast {...toast} onDismiss={dismissToast} />}
      </BwjToastContext.Provider>
    );
  };
  
  // Create a custom hook to consume the ToastContext
  export const useToast = () => {
    const context = useContext(BwjToastContext);
    if (!!context === false) {
      throw new Error('useToast hook must be used by a child component within a DbxToastMessageContext');
    }
    return context!;
  };
  
  